import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Zap, Heart, Dumbbell, Moon, Sparkles, Shield, Search, ShoppingCart, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { RecentlyViewed } from '../components/RecentlyViewed';
import { ReviewSection } from '../components/ReviewSection';
import { SubscribeForm } from '../components/SubscribeForm';
import { AmbassadorSignup } from '../components/AmbassadorSignup';
import { DnaHelix } from '../components/DnaHelix';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const HERO_BG = "https://customer-assets.emergentagent.com/job_5be9d9ba-e756-4119-8306-729d4aea9d0d/artifacts/eys2ynsf_C65FBA21-6FC7-4E26-B556-5F7A70C57700.png";

const benefits = [
  { icon: Zap, title: "Energy & Vitality", description: "Support your body's natural energy production", details: "Peptides like NAD+, MOTS-C, and 5-Amino-1MQ work at the mitochondrial level to optimize how your cells produce energy. NAD+ is essential for cellular metabolism and declines with age — supplementing it may restore youthful energy levels. MOTS-C activates AMPK pathways to improve exercise capacity and metabolic efficiency. These compounds don't just mask fatigue — they target the root cause of low energy at the cellular level, helping your body produce and utilize ATP more effectively throughout the day." },
  { icon: Dumbbell, title: "Muscle & Recovery", description: "Enhanced recovery and performance support", details: "BPC-157, TB-500, and IGF-1 LR3 are among the most researched peptides for muscle repair and recovery. BPC-157 accelerates healing of muscles, tendons, and ligaments by promoting angiogenesis (new blood vessel formation) and reducing inflammation at injury sites. TB-500 supports tissue repair through upregulation of actin, a cell-building protein. IGF-1 LR3 enhances protein synthesis and may promote satellite cell activation for muscle growth. Together, these peptides can significantly reduce recovery time and support peak athletic performance." },
  { icon: Heart, title: "Fat Metabolism", description: "Support healthy metabolic function", details: "Retatrutide, Semaglutide, Tirzepatide, and AOD-9604 target fat loss through distinct mechanisms. Retatrutide is a triple-agonist (GLP-1/GIP/Glucagon) that has shown remarkable results in clinical trials for weight reduction and metabolic improvement. Semaglutide and Tirzepatide are GLP-1 receptor agonists that reduce appetite and improve insulin sensitivity. AOD-9604 is a modified fragment of growth hormone that stimulates lipolysis (fat breakdown) without affecting blood sugar or muscle mass. 5-Amino-1MQ blocks the NNMT enzyme in fat cells, restoring NAD+ levels and boosting metabolic rate. These peptides offer targeted approaches to body composition optimization." },
  { icon: Sparkles, title: "Skin & Anti-Aging", description: "Promote skin health and rejuvenation", details: "GHK-Cu, SNAP-8, Epitalon, and our GLOW blend are at the forefront of anti-aging peptide research. GHK-Cu is a copper peptide that stimulates collagen and elastin production, tightens skin, and reduces fine lines. SNAP-8 works like a topical Botox alternative, relaxing facial muscles to smooth expression wrinkles. Epitalon activates telomerase to protect telomere length — essentially slowing cellular aging. These peptides address aging from multiple angles: surface-level wrinkles, deep collagen loss, and cellular longevity." },
  { icon: Moon, title: "Sleep & Recovery", description: "Improved sleep quality and restoration", details: "DSIP (Delta Sleep-Inducing Peptide) is specifically studied for its ability to promote deeper, more restorative sleep. It modulates sleep architecture by enhancing delta wave activity — the deepest phase of sleep where your body does most of its repair work. Selank and Semax also support sleep quality indirectly by reducing anxiety and stress hormones that interfere with restful sleep. Better sleep means better recovery, improved cognitive function, stronger immunity, and more balanced hormone production." },
  { icon: Shield, title: "Joint Support", description: "Connective tissue and joint health", details: "BPC-157 and TB-500 are the gold standard for joint and connective tissue repair. BPC-157 has been shown in research to accelerate healing of tendons, ligaments, and cartilage by promoting growth factor expression and reducing inflammatory markers. TB-500 enhances flexibility and reduces inflammation through actin upregulation, supporting tissue repair at the cellular level. Cartalax is a bioregulator peptide that specifically targets cartilage tissue regeneration. For athletes and active individuals, these peptides offer targeted support for maintaining joint health and mobility." },
];

export const HomePage = () => {
  const [peptides, setPeptides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const [expandedBenefit, setExpandedBenefit] = useState(null);

  // Read search query from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
      setTimeout(() => {
        document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [peptidesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/peptides`),
          axios.get(`${API}/categories`)
        ]);
        setPeptides(peptidesRes.data);
        setCategories(['All', ...categoriesRes.data]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const normalize = (str) => str.toLowerCase().replace(/[-_]/g, ' ').replace(/[^a-z0-9 ]/g, '');

  const filteredPeptides = peptides.filter(p => {
    const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
    if (searchQuery === '') return matchesCategory;
    
    const query = normalize(searchQuery);
    const searchWords = query.split(/\s+/).filter(w => w.length > 0);
    const searchableText = normalize(`${p.name} ${p.dosage} ${p.category} ${p.id} ${p.description}`);
    const matchesSearch = searchWords.every(word => searchableText.includes(word));
    return matchesCategory && matchesSearch;
  });

  // Live search suggestions (top 6 matches)
  const searchSuggestions = searchQuery.trim().length > 0
    ? peptides.filter(p => {
        const query = normalize(searchQuery);
        const searchWords = query.split(/\s+/).filter(w => w.length > 0);
        const searchableText = normalize(`${p.name} ${p.dosage} ${p.category} ${p.id}`);
        return searchWords.every(word => searchableText.includes(word));
      }).slice(0, 6)
    : [];

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToProducts = () => {
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      <meta name="description" content="Canadian Wellness & Fitness offers research peptides in Canada for research and educational purposes. Browse BPC-157, TB-500, CJC-1295, Ipamorelin, Tesamorelin, Epitalon, DSIP and more." />
      <link rel="canonical" href="https://canadianwellness.net/" />
      {/* Hero Section - Banner Logo */}
      <section className="relative bg-black" data-testid="hero-section">
        <div className="max-w-5xl mx-auto px-4">
          <img 
            src={HERO_BG}
            alt="Canadian Wellness & Fitness"
            className="w-full h-auto block"
          />
        </div>
      </section>

      {/* Guidance Section */}
      <section className="py-16 md:py-24 relative overflow-hidden border-t border-b border-[#27272A]" data-testid="guidance-section">
        {/* DNA Background */}
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/5be9d9ba-e756-4119-8306-729d4aea9d0d/images/f911a9a5e4b6c07c4d0d676dfc56f29989a457fd2fd9ef65c5e79ac4dc79091c.png"
            alt=""
            className="w-full h-full object-cover opacity-40"
            style={{ transform: 'scaleX(-1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="font-['Oswald'] text-2xl md:text-4xl font-bold uppercase tracking-tight text-white mb-6 text-center">
            More Than Just Products — <span className="text-[#C41E3A]">We Guide You Every Step of the Way</span>
          </h2>
          <p className="text-gray-300 text-sm md:text-base mb-4">
            Anyone can sell products. What separates us is what happens after you purchase.
          </p>
          <p className="text-gray-300 text-sm md:text-base mb-6">
            We don't just hand you something and leave you guessing — we provide <span className="text-[#D4AF37] font-semibold">hands-on coaching, guidance, and real support</span> so you actually know what you're doing and why you're doing it.
          </p>
          <p className="text-gray-400 text-sm uppercase tracking-wider font-semibold mb-3">From day one, you'll get:</p>
          <ul className="text-gray-300 text-sm md:text-base space-y-2 mb-6">
            <li className="flex items-start gap-2"><span className="text-[#C41E3A] mt-1">&#x2022;</span> Step-by-step help with mixing and reconstitution</li>
            <li className="flex items-start gap-2"><span className="text-[#C41E3A] mt-1">&#x2022;</span> Clear, personalized dosing guidance based on your goals</li>
            <li className="flex items-start gap-2"><span className="text-[#C41E3A] mt-1">&#x2022;</span> Ongoing support so you can adjust, optimize, and stay on track</li>
            <li className="flex items-start gap-2"><span className="text-[#C41E3A] mt-1">&#x2022;</span> Real answers — not guesswork, not forums, not confusion</li>
          </ul>
          <p className="text-gray-300 text-sm md:text-base mb-6">
            Whether your goal is fat loss, performance, recovery, or overall optimization — we help you build a plan that makes sense for <span className="text-[#D4AF37] font-semibold">you</span>.
          </p>
          <div className="text-gray-400 text-sm md:text-base mb-6 space-y-1">
            <p>No more second guessing.</p>
            <p>No more trying to piece things together online.</p>
          </div>
          <p className="text-gray-300 text-sm md:text-base mb-8">
            Just <span className="text-[#D4AF37] font-semibold">simple, guided execution</span> with people who actually know what they're doing.
          </p>
          <div className="border-t border-[#27272A] pt-6 text-center">
            <p className="text-white text-base md:text-lg font-semibold italic">
              We don't just offer products — we offer <span className="text-[#C41E3A]">results through proper guidance</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Follow Us on Social Media */}
      <section className="py-12 md:py-16 relative" data-testid="social-media-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Oswald'] text-2xl md:text-3xl uppercase tracking-wide text-white mb-3">
            Follow Us on <span className="text-[#C41E3A]">Social Media</span>
          </h2>
          <p className="text-gray-400 text-sm mb-8">Stay updated with the latest products, promotions, and research.</p>
          <div className="flex justify-center gap-6">
            <a
              href="https://www.instagram.com/canadianwellnessandfitness"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-[#0A0A0A] border border-[#27272A] hover:border-[#C41E3A] rounded-sm px-6 py-4 transition-all duration-300 group"
              data-testid="social-instagram"
            >
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#E1306C] group-hover:scale-110 transition-transform">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
              <span className="text-gray-300 group-hover:text-white text-sm font-semibold uppercase tracking-wider transition-colors">Instagram</span>
            </a>
            <a
              href="https://www.facebook.com/share/g/18dFmzugdi/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-[#0A0A0A] border border-[#27272A] hover:border-[#C41E3A] rounded-sm px-6 py-4 transition-all duration-300 group"
              data-testid="social-facebook"
            >
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" className="text-[#1877F2] group-hover:scale-110 transition-transform">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
              <span className="text-gray-300 group-hover:text-white text-sm font-semibold uppercase tracking-wider transition-colors">Facebook</span>
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-12 md:py-16 relative border-t border-[#27272A]/50" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-['Oswald'] text-2xl md:text-3xl uppercase tracking-wide text-white mb-3">
            Be the <span className="text-[#C41E3A]">First to Know</span>
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Subscribers get early access to deals, new products, and promos — before anyone else.
          </p>
          <SubscribeForm source="homepage" />
        </div>
      </section>

      {/* Ambassador Signup */}
      <section id="ambassador" className="py-12 md:py-16 relative border-t border-[#27272A]/50" data-testid="ambassador-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-['Oswald'] text-2xl md:text-3xl uppercase tracking-wide text-white mb-3">
            Become a <span className="text-[#C41E3A]">CWF Ambassador</span>
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Create your own referral code in seconds. Friends save 10% — you earn points toward 20% off. Bring a friend. Earn points. Get rewarded.
          </p>
          <AmbassadorSignup />
        </div>
      </section>

      {/* Customer Reviews */}
      <ReviewSection />

      {/* How to Order Section */}
      <section id="how-to-order" className="py-16 md:py-24 relative overflow-hidden" data-testid="how-to-order-section">
        {/* DNA Background */}
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/5be9d9ba-e756-4119-8306-729d4aea9d0d/images/f911a9a5e4b6c07c4d0d676dfc56f29989a457fd2fd9ef65c5e79ac4dc79091c.png"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-[#050505]" />
        </div>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="font-['Oswald'] text-2xl md:text-4xl font-bold uppercase tracking-tight text-white mb-8 text-center">
            How to <span className="text-[#C41E3A]">Place an Order</span>
          </h2>
          <ol className="text-gray-300 text-sm md:text-base space-y-3 list-decimal list-inside mb-6">
            <li>Browse our catalog and click <span className="text-[#D4AF37] font-semibold">"Add to Cart"</span> on the products you want.</li>
            <li>Go to your <span className="text-[#D4AF37] font-semibold">Cart</span> to review your items, adjust quantities, and fill in your mailing address.</li>
            <li>Click <span className="text-[#D4AF37] font-semibold">"Place Order"</span> — your order will be sent to us automatically and you will receive a confirmation email with your unique order number.</li>
            <li>Send an Interac e-Transfer for your total to <span className="text-[#D4AF37] font-semibold">canadianwellness2026@outlook.com</span> and include your <span className="text-[#D4AF37] font-semibold">order number</span> in the message box.</li>
            <li>Once your e-Transfer has been received and confirmed, we will process your order and send you a tracking number.</li>
          </ol>
          <ul className="text-gray-300 text-sm md:text-base space-y-2 mb-6">
            <li>Flat rate shipping is <span className="text-[#D4AF37] font-semibold">$20</span>.</li>
            <li>Bac water <span className="text-[#D4AF37] font-semibold">$25</span> (30ml) — Pfizer BAC water works for all peptides.</li>
            <li>We currently ship within <span className="text-[#D4AF37] font-semibold">Canada only</span>.</li>
            <li><span className="text-[#D4AF37] font-semibold">COAs available upon request</span>.</li>
            <li>All peptides must be <span className="text-[#D4AF37] font-semibold">refrigerated</span> upon receipt.</li>
          </ul>
          <div className="text-center">
            <Link
              to="/#products"
              onClick={scrollToProducts}
              className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-all duration-300 cursor-pointer"
              data-testid="hero-shop-now-button"
            >
              <ShoppingCart size={16} /> Shop Now
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Oswald'] text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4">
              Potential <span className="text-[#C41E3A]">Benefits</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Peptides support various aspects of health and wellness. Benefits vary by peptide and individual goals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className="feature-card rounded-sm animate-fade-in-up overflow-hidden cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`benefit-${index}`}
                onClick={() => setExpandedBenefit(expandedBenefit === index ? null : index)}
              >
                <div className="p-6 md:p-8 flex items-start justify-between">
                  <div>
                    <benefit.icon size={32} className="text-[#C41E3A] mb-4" />
                    <h3 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {benefit.description}
                    </p>
                  </div>
                  <ChevronDown
                    size={20}
                    className={`text-gray-500 mt-1 flex-shrink-0 ml-3 transition-transform duration-300 ${expandedBenefit === index ? 'rotate-180 text-[#D4AF37]' : ''}`}
                  />
                </div>
                {expandedBenefit === index && (
                  <div className="px-6 md:px-8 pb-6 md:pb-8 pt-0 border-t border-[#1a1a1a]">
                    <p className="text-gray-300 text-sm leading-relaxed pt-4">
                      {benefit.details}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recently Viewed Section */}
      <RecentlyViewed />

      {/* Products Section */}
      <section id="products" className="py-20 md:py-32 bg-[#050505]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Oswald'] text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4">
              Research <span className="text-[#C41E3A]">Catalog</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              High-purity peptides for scientific application. Click on any product to learn more.
            </p>
          </div>

          {/* Branded Vial Image */}
          <div className="flex justify-center mb-10">
            <img
              src="https://customer-assets.emergentagent.com/job_5be9d9ba-e756-4119-8306-729d4aea9d0d/artifacts/j2u0uv55_731BD927-307D-46EC-A7B1-6E72488E9A62.png"
              alt="Canadian Wellness & Fitness Vial"
              className="w-64 md:w-80 h-auto"
              style={{ filter: 'drop-shadow(0 0 30px rgba(196,30,58,0.25))' }}
              data-testid="branded-vial-image"
            />
          </div>

          {/* Live Search Bar */}
          <div className="max-w-md mx-auto mb-8 relative" ref={searchRef} data-testid="search-container">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
              <Input
                type="text"
                placeholder="Search peptides..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchFocused(true); }}
                onFocus={() => setSearchFocused(true)}
                className="pl-10 pr-10 bg-[#121212] border-[#27272A] text-white placeholder:text-gray-500 focus:border-[#C41E3A] focus:ring-[#C41E3A]"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setSearchFocused(false); }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
                  data-testid="search-clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Live Suggestions Dropdown */}
            {searchFocused && searchSuggestions.length > 0 && (
              <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-[#121212] border border-[#27272A] rounded-md shadow-2xl overflow-hidden" data-testid="search-suggestions">
                {searchSuggestions.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    onClick={() => { setSearchFocused(false); setSearchQuery(''); }}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#1a1a1a] transition-colors border-b border-[#27272A] last:border-b-0"
                    data-testid={`suggestion-${p.id}`}
                  >
                    <img src={p.image} alt={p.name} className="w-10 h-10 object-contain rounded bg-white/5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{p.name}</p>
                      <p className="text-gray-500 text-xs">{p.dosage} — {p.category}</p>
                    </div>
                    <span className="text-[#C41E3A] text-sm font-bold">{p.price}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="spinner" data-testid="loading-spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
              {filteredPeptides.map((peptide, index) => (
                <div
                  key={peptide.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <ProductCard peptide={peptide} />
                </div>
              ))}
            </div>
          )}

          {filteredPeptides.length === 0 && !loading && (
            <div className="text-center py-12" data-testid="no-results">
              <Search size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No products found matching "{searchQuery || selectedCategory}"</p>
              <button 
                onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
                className="mt-4 text-[#C41E3A] hover:text-[#A01830] font-semibold"
                data-testid="clear-search"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* End of content */}
    </div>
  );
};

export default HomePage;
